import clsx from "clsx";
import { FormHeader } from "app/components/atoms/layout";
import { Select } from "app/components/atoms/select";
import Table from "app/components/atoms/table";
import api from "app/integration/api";
import { useFetcher } from "app/integration/common/hooks";
import MainLayout from "app/layouts/main";
import { useEffect, useState } from "react";
import * as XLSX from "xlsx";

const breadcrumbItems = [
  { label: "Laporan Toko", href: "#" },
  { label: "Stok Outlet", href: "#" },
];

const tabs = [
  { id: "non_unique", label: "Barang per-Qty" },
  { id: "unique", label: "Barang Unik/Barcode" },
];

function StoreReportStockPage() {
  const [warehouses, setWarehouses] = useState([]);
  const [activeTab, setActiveTab] = useState("non_unique");
  const [queryParams, setQueryParams] = useState({
    warehouse_id: null,
  });

  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        const response = await api.warehouse.index({ limit: 1000 });
        setWarehouses(response?.data ?? []);
      } catch (err) {
        setWarehouses([]);
      }
    };
    loadWarehouses();
  }, []);

  const warehouseOptions = [
    { value: null, label: "Semua Outlet" },
    ...warehouses.map((warehouse) => ({
      value: warehouse.id,
      label: warehouse.name,
    })),
  ];

  const [data, , loader] = useFetcher({
    modelName: "storeReport",
    methodName: "outletStock",
    payload: { id: queryParams },
  });

  const currentRows = Array.isArray(data?.[activeTab]) ? data[activeTab] : [];

  const exportExcel = () => {
    const dataForExcel = currentRows.map((row) => ({
      Outlet: row.warehouse_name,
      "Kode Produk": row.product_code,
      Produk: row.product_name,
      Size: row.size_name ?? "-",
      Warna: row.color_name ?? "-",
      Stok: row.on_hand,
    }));
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(dataForExcel, {
      header: ["Outlet", "Kode Produk", "Produk", "Size", "Warna", "Stok"],
    });
    worksheet["!cols"] = [
      { wch: 22 },
      { wch: 16 },
      { wch: 30 },
      { wch: 10 },
      { wch: 14 },
      { wch: 10 },
    ];
    const sheetName =
      tabs.find((tab) => tab.id === activeTab)?.label ?? "Stok";
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const formattedDate = new Date().toISOString().split("T")[0];
    link.download = `laporan_stok_outlet_${activeTab}_${formattedDate}.xlsx`;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
  };

  return (
    <MainLayout
      activeSidebarNavigation="store-report"
      activeSidebarChild="outlet-stock"
      breadcrumbItems={breadcrumbItems}
      pageTitle="Laporan Stok Outlet"
      headingButtons={[
        {
          label: "Export Excel",
          type: "button",
          onClick: exportExcel,
        },
      ]}
    >
      <Table.Container>
        <Table.SearchBarWrapper>
          <FormHeader>
            <Select testId="store-report-stock-outlet-select"
              label="Outlet"
              placeholder="Filter Outlet"
              options={warehouseOptions}
              value={queryParams.warehouse_id}
              onChange={(e) =>
                setQueryParams({
                  ...queryParams,
                  warehouse_id: e.target.value,
                })
              }
              absolute={false}
            />
          </FormHeader>
        </Table.SearchBarWrapper>
        <div className="flex gap-2 px-2 pt-3">
          {tabs.map((tab) => (
            <button data-testid={`store-report-stock-item-button-${tab.id}`}
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={clsx(
                "px-4 py-2 text-sm font-medium rounded-t-lg border-b-2",
                activeTab === tab.id
                  ? "border-primary-600 text-primary-600 dark:text-primary-400"
                  : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400"
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <Table.Wrapper asIndex={true}>
          <Table.Header>
            <Table.HeaderRow>
              <Table.Heading>Outlet</Table.Heading>
              <Table.Heading>Kode Produk</Table.Heading>
              <Table.Heading>Produk</Table.Heading>
              <Table.Heading>Size</Table.Heading>
              <Table.Heading>Warna</Table.Heading>
              <Table.Heading>
                <p className="text-right">Stok</p>
              </Table.Heading>
            </Table.HeaderRow>
          </Table.Header>
          <Table.Body>
            {currentRows.map((row, index) => (
              <Table.BodyRow key={index}>
                <Table.Cell>{row.warehouse_name}</Table.Cell>
                <Table.Cell>{row.product_code}</Table.Cell>
                <Table.Cell>{row.product_name}</Table.Cell>
                <Table.Cell>{row.size_name ?? "-"}</Table.Cell>
                <Table.Cell>{row.color_name ?? "-"}</Table.Cell>
                <Table.Cell fontWeight="semibold">
                  <p className="text-right">{row.on_hand}</p>
                </Table.Cell>
              </Table.BodyRow>
            ))}
          </Table.Body>
        </Table.Wrapper>
      </Table.Container>
      {loader.state && (
        <p className="text-sm px-2 py-1 text-gray-500 font-light">
          Memuat data...
        </p>
      )}
      {currentRows.length === 0 && !loader.state && (
        <p className="text-sm px-2 py-1 text-gray-500 font-light">
          Tidak ada data untuk filter ini.
        </p>
      )}
    </MainLayout>
  );
}

export default StoreReportStockPage;
